// src/pages/admin/OrgPendingInvites.jsx
import { useState } from "react";
import { AlertTriangle, RefreshCcw, X } from "lucide-react";
import { fetchWithAuth } from "../../api/fetchWithAuth";

/**
 * The pending-invitation list half of the org-admin invite UX. Split out of
 * the former OrgInvitesPanel so it can scroll alongside the users table while
 * OrgInviteForm stays pinned above it.
 *
 * The `invites` array is owned by OrgAdminPage — same as `users` and
 * `license` — because a create here has to refresh all three in lock-step.
 *
 * Props:
 *   invites   — [{ id, email, role, tier_code, accepted_at, revoked_at,
 *                  last_error, last_attempt_at }]
 *   onChanged — called after a successful resend/revoke so the parent can
 *               refresh invites + users + license counts together
 */
export default function OrgPendingInvites({ invites = [], onChanged }) {
  const [rowError, setRowError] = useState(null); // { id, message }

  function notifyParent() {
    if (typeof onChanged === "function") {
      Promise.resolve(onChanged()).catch((err) =>
        console.error("[invites] onChanged callback failed", err)
      );
    }
  }

  async function resendInvite(inviteId) {
    setRowError(null);
    try {
      const res = await fetchWithAuth(
        `/api/v1/admin/org-invites/${inviteId}/resend`,
        { method: "POST" }
      );
      if (!res.ok) {
        setRowError({ id: inviteId, message: `Resend failed (${res.status})` });
        return;
      }
      notifyParent();
    } catch (e) {
      console.error("[invites] resend error", e);
      setRowError({ id: inviteId, message: "Network error" });
    }
  }

  async function revokeInvite(inviteId) {
    setRowError(null);
    try {
      const res = await fetchWithAuth(
        `/api/v1/admin/org-invites/${inviteId}/revoke`,
        { method: "POST" }
      );
      if (!res.ok) {
        setRowError({ id: inviteId, message: `Revoke failed (${res.status})` });
        return;
      }
      notifyParent();
    } catch (e) {
      console.error("[invites] revoke error", e);
      setRowError({ id: inviteId, message: "Network error" });
    }
  }

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-gray-200">
        Pending Invitations
      </h3>

      <div className="divide-y divide-slate-800">
        {invites.map((invite) => {
          const rowHasError = rowError?.id === invite.id;

          // Still pending AND its last accept attempt failed. The accept paths
          // null both columns on success, so this cannot show for an invite
          // that recovered. Without it a permanently-failing invite is
          // indistinguishable from one nobody has opened yet: the auto-accept
          // in middleware/authz.js retries on every authenticated request by
          // the invitee, fails the same way each time, and says so only in the
          // container log.
          const isStuck =
            !invite.accepted_at && !invite.revoked_at && !!invite.last_error;

          return (
            <div
              key={invite.id}
              className="flex items-center justify-between py-2 text-xs"
            >
              <div>
                <div className="text-gray-200">{invite.email}</div>
                <div className="text-fg-secondary font-mono uppercase tracking-wider">
                  {invite.role}
                  {invite.tier_code && (
                    <span className="ml-2 text-accent-info">{invite.tier_code}</span>
                  )}
                </div>
                {rowHasError && (
                  <div className="text-red-400 mt-0.5">{rowError.message}</div>
                )}

                {isStuck && (
                  <div className="mt-1 flex items-start gap-1 text-amber-400">
                    <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                    <span>
                      Could not be accepted
                      {invite.last_attempt_at && (
                        <>
                          {" "}
                          (last tried{" "}
                          {new Date(invite.last_attempt_at).toLocaleString()})
                        </>
                      )}
                      {/* The raw cause. Deliberately shown rather than
                          summarised: the admin cannot act on "something went
                          wrong", and this is the only place the reason
                          surfaces outside the container log. It is written
                          compact and truncated at the source. */}
                      <span className="block break-words text-fg-secondary">
                        {invite.last_error}
                      </span>
                    </span>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2">
                {!invite.accepted_at && !invite.revoked_at && (
                  <>
                    <button
                      onClick={() => resendInvite(invite.id)}
                      className="text-gray-400 hover:text-accent-action"
                      title="Resend invite"
                    >
                      <RefreshCcw size={14} />
                    </button>

                    <button
                      onClick={() => revokeInvite(invite.id)}
                      className="text-gray-400 hover:text-red-400"
                      title="Revoke invite"
                    >
                      <X size={14} />
                    </button>
                  </>
                )}

                {invite.accepted_at && (
                  <span className="text-green-400">Accepted</span>
                )}

                {invite.revoked_at && (
                  <span className="text-fg-secondary">Revoked</span>
                )}
              </div>
            </div>
          );
        })}

        {!invites.length && (
          <div className="text-fg-secondary text-xs py-2">
            No pending invites
          </div>
        )}
      </div>
    </div>
  );
}
